import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner, useAuth, useFetch } from '@greenrinse/ui';
import { downloadAuth } from './download';
import type { Partner, Site } from './types';
import { ReportSubscriptionsPanel } from './ReportSubscriptionsPanel';

export interface EsgKpis { washes: number; vehiclesServed: number; sites: number; waterUsedLitres: number; waterSavedLitres: number; kmAvoided: number; co2SavedKg: number }
export interface EsgRow { id: string; name: string; washes: number; waterUsedLitres: number; waterSavedLitres: number; co2SavedKg: number }
export interface EsgReport {
  operatorName: string; partnerName: string | null; siteName: string | null; from: string; to: string;
  period: EsgKpis; lifetime: EsgKpis; factors: { baselineLitresPerWash: number; defaultLitresPerWash: number; avoidedKmPerWash: number; co2GramsPerKm: number };
  months: { year: number; month: number; washes: number; waterUsedLitres: number; waterSavedLitres: number; co2SavedKg: number }[];
  bySite: EsgRow[]; byPartner: EsgRow[]; byVehicleType: EsgRow[]; generatedAtUtc: string;
}

export const litres = (n: number) => (n >= 1000 ? `${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} m³` : `${Math.round(n).toLocaleString()} L`);
export const kg = (n: number) => (n >= 1000 ? `${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t` : `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`);

/** Month-by-month bars for water saved (brand teal) with the wash count underneath. Pure SVG, no chart library. */
export function MonthlyBars({ months }: { months: EsgReport['months'] }) {
  const max = Math.max(1, ...months.map((m) => m.waterSavedLitres));
  const w = 600, h = 140, pad = 6, gap = 6;
  const bw = (w - pad * 2) / Math.max(1, months.length) - gap;
  return (
    <svg viewBox={`0 0 ${w} ${h + 28}`} style={{ width: '100%', height: 168 }} role="img" aria-label="Water saved per month">
      {months.map((m, i) => {
        const bh = (m.waterSavedLitres / max) * (h - pad * 2);
        const x = pad + i * (bw + gap);
        return (
          <g key={`${m.year}-${m.month}`}>
            <rect x={x} y={h - pad - bh} width={bw} height={bh} rx={4} fill="var(--gr-accent)" opacity={0.85}><title>{`${m.year}-${String(m.month).padStart(2, '0')}: ${litres(m.waterSavedLitres)} · ${m.washes}`}</title></rect>
            <text x={x + bw / 2} y={h + 12} textAnchor="middle" fontSize={10} fill="var(--gr-text-muted)">{new Date(m.year, m.month - 1, 1).toLocaleDateString(undefined, { month: 'short' })}</text>
            <text x={x + bw / 2} y={h + 24} textAnchor="middle" fontSize={9} fill="var(--gr-text-muted)">{m.washes}</text>
          </g>
        );
      })}
    </svg>
  );
}

function Tile({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <Card small style={accent ? { background: 'var(--gr-primary-container)', color: 'var(--gr-on-primary-container)' } : undefined}>
      <div style={{ fontSize: 13, opacity: 0.8 }}>{label}</div>
      <div className="gr-stat">{value}</div>
      {sub && <div style={{ fontSize: 12, opacity: 0.8 }}>{sub}</div>}
    </Card>
  );
}

function Breakdown({ title, rows, t }: { title: string; rows: EsgRow[]; t: (k: string) => string }) {
  if (rows.length === 0) return null;
  return (
    <Card small style={{ padding: 0 }}>
      <div style={{ padding: '10px 14px' }} className="gr-muted">{title}</div>
      <table className="gr-table">
        <thead><tr><th>{t('esg.name')}</th><th style={{ textAlign: 'end' }}>{t('esg.washes')}</th><th style={{ textAlign: 'end' }}>{t('esg.water_used')}</th><th style={{ textAlign: 'end' }}>{t('esg.water_saved')}</th><th style={{ textAlign: 'end' }}>CO₂</th></tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id + r.name}><td>{r.name}</td><td style={{ textAlign: 'end' }}>{r.washes}</td><td style={{ textAlign: 'end' }}>{litres(r.waterUsedLitres)}</td><td style={{ textAlign: 'end' }}>{litres(r.waterSavedLitres)}</td><td style={{ textAlign: 'end' }}>{kg(r.co2SavedKg)}</td></tr>)}</tbody>
      </table>
    </Card>
  );
}

/**
 * Sustainability / ESG page shared by admin (all partners, filters) and partner portal (own data).
 * Everything is computed server-side from the same factors as the PDF.
 */
export default function SustainabilityPage({ partnerMode = false }: { partnerMode?: boolean }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [partnerId, setPartnerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const partners = useFetch<{ items: Partner[] }>(partnerMode ? null : '/api/partners?pageSize=200');
  const sites = useFetch<{ items: Site[] }>('/api/sites?pageSize=300');
  const qs = new URLSearchParams({ from, to, ...(partnerId ? { partnerId } : {}), ...(siteId ? { siteId } : {}) }).toString();
  const rep = useFetch<EsgReport>(`/api/sustainability?${qs}`);

  if (rep.loading && !rep.data) return <Spinner />;
  const x = rep.data;
  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <div><h1 style={{ margin: 0 }}>🌿 {t('esg.title')}</h1><span className="gr-muted">{x?.partnerName ?? x?.operatorName}{x?.siteName ? ` · ${x.siteName}` : ''}</span></div>
        <div className="gr-row">
          <Button variant="outline" style={{ minHeight: 36 }} onClick={() => void downloadAuth(`/api/sustainability/report.pdf?${qs}&lang=${i18n.language}`, `sustainability-${from}-${to}.pdf`)}>{t('esg.download_pdf')}</Button>
          <Button variant="outline" style={{ minHeight: 36 }} onClick={() => void downloadAuth(`/api/sustainability/report.xlsx?${qs}`, `sustainability-${from}-${to}.xlsx`)}>Excel</Button>
        </div>
      </div>
      <div className="gr-row">
        <input className="gr-input" type="date" style={{ width: 'auto', minHeight: 40 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <input className="gr-input" type="date" style={{ width: 'auto', minHeight: 40 }} value={to} onChange={(e) => setTo(e.target.value)} />
        {!partnerMode && <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={partnerId} onChange={(e) => { setPartnerId(e.target.value); setSiteId(''); }}><option value="">{t('esg.all_partners')}</option>{(partners.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
        <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={siteId} onChange={(e) => setSiteId(e.target.value)}><option value="">{t('partner.all_sites')}</option>{(sites.data?.items ?? []).filter((s) => !partnerId || s.partnerId === partnerId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
      </div>
      {rep.error && <Alert tone="error">{rep.error}</Alert>}
      {x && (
        <>
          <div className="gr-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
            <Tile label={t('esg.washes')} value={x.period.washes.toLocaleString()} sub={`${x.period.vehiclesServed} ${t('partner.vehicles')} · ${x.period.sites} ${t('esg.sites')}`} />
            <Tile accent label={`💧 ${t('esg.water_saved')}`} value={litres(x.period.waterSavedLitres)} sub={t('esg.vs_baseline', { baseline: x.factors.baselineLitresPerWash })} />
            <Tile label={`🌍 ${t('esg.co2_avoided')}`} value={kg(x.period.co2SavedKg)} sub={t('esg.km_not_driven', { km: Math.round(x.period.kmAvoided).toLocaleString() })} />
            <Tile label={t('esg.lifetime')} value={litres(x.lifetime.waterSavedLitres)} sub={`${x.lifetime.washes.toLocaleString()} ${t('esg.washes').toLowerCase()} · ${kg(x.lifetime.co2SavedKg)} CO₂`} />
          </div>
          <Card small><div className="gr-muted" style={{ marginBottom: 6 }}>{t('esg.month_by_month')}</div><MonthlyBars months={x.months} /></Card>
          <div className="gr-form-grid">
            <Breakdown title={t('esg.by_site')} rows={x.bySite} t={t} />
            {!partnerMode && <Breakdown title={t('esg.by_partner')} rows={x.byPartner} t={t} />}
            <Breakdown title={t('esg.by_vehicle_type')} rows={x.byVehicleType} t={t} />
          </div>
          <p className="gr-muted" style={{ fontSize: 12 }}>{t('esg.methodology', { baseline: x.factors.baselineLitresPerWash, dflt: x.factors.defaultLitresPerWash, km: x.factors.avoidedKmPerWash, g: x.factors.co2GramsPerKm })}</p>
        </>
      )}
      {(partnerMode || ['SuperAdmin', 'RegionalManager', 'Finance', 'OperationsSupervisor'].includes(user?.role ?? '')) && (
        <ReportSubscriptionsPanel kinds={partnerMode ? ['Sustainability', 'Reconciliation'] : ['Sustainability']} compact />
      )}
    </div>
  );
}
